/**
 * Kidswear silhouettes: kidswear, kids_romper, frock.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { beltLoops, neckLabel } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Kidswear (child's tee + trouser set) ─────────────────────────── */
export function kidswearFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 100;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 154 86 L 112 102 L 78 154 L 96 174 L 140 156 L 136 252 Q 200 262 264 252 L 260 156 L 304 174 L 322 154 L 288 102 L 246 86 Q 200 ${neckDip - 20} 154 86 Z"/>
  <path ${DETAIL_ATTRS} d="M 154 86 Q 200 ${neckDip} 246 86"/>
  <path ${STITCH_ATTRS} d="M 158 91 Q 200 ${neckDip + 6} 242 91"/>
  <path ${DETAIL_ATTRS} d="M 78 154 L 96 174"/>
  <path ${DETAIL_ATTRS} d="M 322 154 L 304 174"/>
  <path ${STITCH_ATTRS} d="M 85 150 L 102 168"/>
  <path ${STITCH_ATTRS} d="M 315 150 L 298 168"/>
  <path ${STITCH_ATTRS} d="M 112 104 Q 130 130 140 156"/>
  <path ${STITCH_ATTRS} d="M 288 104 Q 270 130 260 156"/>
  <path ${STITCH_ATTRS} d="M 137 244 Q 200 254 263 244"/>
  ${
    view === "front"
      ? `<rect x="186" y="92" width="28" height="11" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 148 122 L 252 122"/>
  ${neckLabel(200, 86)}`
  }
  <path ${OUTLINE_ATTRS} d="M 140 274 L 260 274 L 264 296 L 272 438 L 222 438 L 212 340 Q 200 326 188 340 L 178 438 L 128 438 L 136 296 Z"/>
  <path ${DETAIL_ATTRS} d="M 136 296 L 264 296"/>
  ${beltLoops([158, 182, 218, 242], 272, 26)}
  <path ${STITCH_ATTRS} d="M 142 278 L 258 278"/>
  <path ${STITCH_ATTRS} d="M 138 292 L 262 292"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 296 L 200 320"/>
  <path ${STITCH_ATTRS} d="M 195 298 Q 190 312 188 326"/>`
      : `<path ${DETAIL_ATTRS} d="M 142 332 Q 200 348 258 332"/>
  <path ${STITCH_ATTRS} d="M 144 336 Q 200 352 256 336"/>`
  }
  <path ${STITCH_ATTRS} d="M 130 430 L 176 430"/>
  <path ${STITCH_ATTRS} d="M 224 430 L 270 430"/>
</g>`;
}

/* ── Kids romper (one-piece, snap buttons, raglan sleeves) ──────── */
export function kids_romperFlat(view: FlatView): string {
  const neckDip = view === "front" ? 114 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 86 L 116 102 L 82 152 L 100 172 L 142 154 L 142 246 L 138 340 L 188 340 L 196 274 Q 200 262 204 274 L 212 340 L 262 340 L 258 246 L 258 154 L 300 172 L 318 152 L 284 102 L 242 86 Q 200 ${neckDip - 20} 158 86 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 86 Q 200 ${neckDip} 242 86"/>
  <path ${STITCH_ATTRS} d="M 161 91 Q 200 ${neckDip + 6} 239 91"/>
  <path ${DETAIL_ATTRS} d="M 82 152 L 100 172"/>
  <path ${DETAIL_ATTRS} d="M 318 152 L 300 172"/>
  <path ${STITCH_ATTRS} d="M 89 148 L 106 166"/>
  <path ${STITCH_ATTRS} d="M 311 148 L 294 166"/>
  <path ${DETAIL_ATTRS} d="M 142 246 L 258 246"/>
  <path ${STITCH_ATTRS} d="M 144 250 L 256 250"/>
  ${
    view === "front"
      ? `<circle cx="170" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="230" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>
  <rect x="186" y="92" width="28" height="10" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 150 120 L 250 120"/>
  ${neckLabel(200, 86)}
  <circle cx="170" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="230" cy="260" r="3" ${DETAIL_FILLED_ATTRS}/>`
  }
  <path ${STITCH_ATTRS} d="M 140 332 L 186 332"/>
  <path ${STITCH_ATTRS} d="M 214 332 L 260 332"/>
</g>`;
}

/* ── Frock (child's dress, puff sleeves, gathered skirt) ──────────── */
export function frockFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 98;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 82 L 118 98 Q 98 114 108 138 Q 118 150 132 146 L 136 222 L 96 400 Q 200 414 304 400 L 264 222 L 268 146 Q 282 150 292 138 Q 302 114 282 98 L 242 82 Q 200 ${neckDip - 20} 158 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 82 Q 200 ${neckDip} 242 82"/>
  <path ${STITCH_ATTRS} d="M 161 87 Q 200 ${neckDip + 6} 239 87"/>
  <path ${STITCH_ATTRS} d="M 102 132 Q 114 146 128 142"/>
  <path ${STITCH_ATTRS} d="M 298 132 Q 286 146 272 142"/>
  <path ${STITCH_ATTRS} d="M 118 100 Q 128 120 132 146"/>
  <path ${STITCH_ATTRS} d="M 282 100 Q 272 120 268 146"/>
  <path ${DETAIL_ATTRS} d="M 136 222 L 264 222"/>
  <path ${STITCH_ATTRS} d="M 138 226 L 262 226"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 156 220 L 160 162"/>
  <path ${STITCH_ATTRS} d="M 244 220 L 240 162"/>
  <path ${DETAIL_ATTRS} d="M 110 100 Q 124 104 134 98"/>
  <path ${DETAIL_ATTRS} d="M 290 100 Q 276 104 266 98"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 92 L 200 396"/>
  <path ${STITCH_ATTRS} d="M 158 218 L 162 160"/>
  <path ${STITCH_ATTRS} d="M 242 218 L 238 160"/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 100 392 Q 200 406 300 392"/>
  <path ${STITCH_ATTRS} d="M 98 396 Q 200 410 302 396"/>
</g>`;
}
